///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, input, OnInit } from '@angular/core';
import { EngineProcessTypeEnum, PlannerFeatureCollectionDTO, ProjectDTO } from '../../../../generated/shared-types.model';
import {
    ItemDirection,
    SuiNumberControlComponent,
    SuiRadioGroupControlComponent,
    SuiStringControlComponent,
    SuiToggleControlComponent,
    SuiTypeRadio,
    SuiTypeToggle
} from '@siklu/sui-lib/infrastructure';
import { AuthorizationManagerService } from '../../../../shared/auth/authorization-manager/authorization-manager.service';
import { isDefined } from '@siklu/sui-lib/utils';
import { SuiTranslatePipe } from '@siklu/sui-lib';
import { SuiConfigPropertiesDTO, SuiProjectDTO } from '../../../../generated/shared-sui-types.model';
import { ProjectUnits } from '../../../project-enums';

@Component({
    selector: 'winde-project-dialog-general-tab',
    templateUrl: 'project-dialog-general-tab.component.html',
    styleUrls: ['../../../projects.scss'],
    imports: [
        SuiTranslatePipe,
        SuiToggleControlComponent,
        SuiRadioGroupControlComponent,
        SuiStringControlComponent,
        SuiNumberControlComponent
    ]
})
export class ProjectDialogGeneralTabComponent implements OnInit {
    viewInReadOnly = input<boolean>();
    project = input<ProjectDTO>();
    logicalConfig = input<PlannerFeatureCollectionDTO>();

    suiProject: SuiProjectDTO;
    suiLogicalConfig: SuiConfigPropertiesDTO;

    public hasStationsWithLayers = false;
    public inputProductUnits: SuiTypeRadio;
    public areaPlannerProjectToggle: SuiTypeToggle;

    constructor(public readonly authorizationManagerService: AuthorizationManagerService) {}

    ngOnInit(): void {
        this.hasStationsWithLayers = this.projectHasStationsWithLayers();

        this.suiProject = new SuiProjectDTO(this.project());
        this.suiLogicalConfig = new SuiConfigPropertiesDTO(this.logicalConfig().crs.properties);

        this.suiProject.name.readonly = this.viewInReadOnly();
        this.suiProject.description.readonly = this.viewInReadOnly();
        this.suiLogicalConfig.basic_discount.readonly = this.viewInReadOnly();
        this.suiLogicalConfig.layersModeEnabled.readonly = this.viewInReadOnly();
        this.suiLogicalConfig.locationNamePrefix.readonly = this.viewInReadOnly();

        this.inputProductUnits = new SuiTypeRadio(
            this.logicalConfig().crs.properties.productUnits,
            [ProjectUnits.METER, ProjectUnits.FEET],
            'ProjectUnits',
            ItemDirection.HORIZONTAL
        );
        this.inputProductUnits.applyModifiedStateStyle = false;
        this.inputProductUnits.readonly = this.viewInReadOnly();

        this.suiLogicalConfig.layersModeEnabled.readonly = this.viewInReadOnly() || this.hasStationsWithLayers;

        this.areaPlannerProjectToggle = new SuiTypeToggle(this.project().engineType === EngineProcessTypeEnum.AREA_PLANNER);
        this.areaPlannerProjectToggle.applyModifiedStateStyle = false;
        this.areaPlannerProjectToggle.readonly = isDefined(this.project().id);
    }

    public validateAndApply(): boolean {
        if (!this.suiProject.validateAndShowError() || !this.suiLogicalConfig.validateAndShowError()) {
            return false;
        }

        this.project().name = this.suiProject.name.getValue();
        this.project().description = this.suiProject.description.getValue();

        this.logicalConfig().crs.properties.basic_discount = this.suiLogicalConfig.basic_discount.getValue();
        this.logicalConfig().crs.properties.layersModeEnabled = this.suiLogicalConfig.layersModeEnabled.getValue();
        this.logicalConfig().crs.properties.productUnits = this.inputProductUnits.getValue();
        this.logicalConfig().crs.properties.locationNamePrefix = this.suiLogicalConfig.locationNamePrefix.getValue();

        this.project().engineType = this.areaPlannerProjectToggle.value
            ? EngineProcessTypeEnum.AREA_PLANNER
            : EngineProcessTypeEnum.RINGDES_LATEST;

        return true;
    }

    private projectHasStationsWithLayers(): boolean {
        if (this.logicalConfig()?.crs?.properties?.nodesCountPerLayer) {
            for (let i = 1; i < this.logicalConfig().crs.properties.nodesCountPerLayer.length; i++) {
                if (this.logicalConfig().crs.properties.nodesCountPerLayer[i] > 0) {
                    return true;
                }
            }
        }

        return false;
    }
}
